import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { isEUDomain, isWorldPanel } from '@helpers';

import {
  CreditsIcon,
  HelpV2Icon,
  NotificationIcon,
  PaletteIcon,
  ProductsIcon,
  SubscriptionIcon,
  WorldpanelLogo,
} from '@utilities';
import { readAllNotifications, readNotifications } from '@api/notifications';
import { transformNotificationData } from '@api/notifications/utilities/helpers';

import AccountDropdown from './components/AccountDropdown';
import HelpDropdown from './components/HelpDropdown';
import NotificationDropdown from './components/NotificationDropdown';
import ProductsDropdown from './components/ProductsDropdown';
import ThemeDropdown from './components/ThemeDropdown';
import VerifiedVoicesOverviewDropdown from './components/VerifiedVoicesOverviewDropdown';

import { default as NumeratorLogo } from './assets/logo.svg?react';

import styles from './_index.module.scss';

const Header = ({ routes }) => {
  const [hasAllNotifications, setHasAllNotifications] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState('');
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);

  const userData = useSelector((state) => state?.user?.data);
  const { notification } = useSelector((state) => state?.websocket);
  const firstName = userData?.firstName;
  const hasSubscription =
    userData?.permissions?.canAccessSurveySubscription &&
    userData?.clientInfo?.subscriptions?.length > 0;
  const worldPanel = isWorldPanel();

  const { pathname } = useLocation();
  const { pathnameBase: url } = useMatch('/dashboard/*');
  const verifiedVoicesDropdown = useRef(null);
  const helpDropdown = useRef(null);
  const notificationDropdown = useRef(null);
  const productDropdown = useRef(null);
  const settingsDropdown = useRef(null);
  const themeDropdown = useRef(null);

  const handleReadAllNotifications = async () => {
    try {
      await readAllNotifications();
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    const getNotifications = async () => {
      try {
        setIsLoading(true);
        const response = await readNotifications({ page });
        const results = response?.data?.results;
        setNotifications([...notifications, ...results]);
        setHasNewNotification(results.some((notification) => notification.isRead === false));
        if (!response?.data?.meta?.next) setHasAllNotifications(true);
        setIsLoading(false);
      } catch (error) {
        console.warn(error);
      }
    };
    getNotifications();
  }, [page]);

  useEffect(() => {
    if (notification?.type === 'notification_center') {
      const newNotifications = [transformNotificationData(notification), ...notifications];
      setNotifications(newNotifications);
      setHasNewNotification(newNotifications.some((notification) => notification.isRead === false));
    }
  }, [notification]);

  return (
    <header
      className={`${styles['header']}${worldPanel ? ` ${styles['header-worldpanel']}` : ''}`}
      data-testid="header"
    >
      <div className={styles['header-content']} data-testid="header-content">
        <div>
          <Link data-testid="header-content-logo-link" to={url}>
            {worldPanel ? (
              <WorldpanelLogo aria-label="Worldpanel by Numerator" role="img" />
            ) : (
              <NumeratorLogo />
            )}
          </Link>
          <ul data-testid="header-content-routes">
            {routes.map((route, routeIndex) => {
              const link = `${url}/${route?.path}`;
              const isActive = pathname?.includes(link) ? styles['is-active'] : '';

              return route?.is_hidden ? null : (
                <li
                  className={isActive}
                  data-testid={`header-content-${route?.id}`}
                  key={routeIndex}
                >
                  <Link className={isActive} to={link}>
                    {route?.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <ul className={styles['header-icon-actions']}>
            {/* World Panel (VN) shows a reduced icon set; hide these on VN. */}
            {!worldPanel && (
              <>
                <li>
                  <button
                    aria-label="Verified Voices Overview button"
                    className={styles['header-icon-button']}
                    data-testid="header-content-verified-voices-icon"
                    onClick={() => setActiveDropdown('verified-voices-overview')}
                    ref={verifiedVoicesDropdown}
                  >
                    {hasSubscription ? <SubscriptionIcon /> : <CreditsIcon />}
                  </button>
                  <VerifiedVoicesOverviewDropdown
                    hasSubscription={hasSubscription}
                    isVVOverviewDropdownActive={activeDropdown === 'verified-voices-overview'}
                    setActiveDropdown={setActiveDropdown}
                    target={verifiedVoicesDropdown.current}
                  />
                </li>
                <li>
                  <button
                    aria-label="Help button"
                    className={styles['header-icon-button']}
                    data-testid="header-content-help-icon"
                    onClick={() => setActiveDropdown('help')}
                    ref={helpDropdown}
                  >
                    <HelpV2Icon />
                  </button>
                  <HelpDropdown
                    isHelpDropdownActive={activeDropdown === 'help'}
                    menuLabel={userData?.menu_label}
                    setActiveDropdown={setActiveDropdown}
                    target={helpDropdown.current}
                  />
                </li>
                {!isEUDomain() && (
                  <li>
                    <button
                      aria-label="Other products button"
                      className={styles['header-icon-button']}
                      data-testid="header-content-product-icon"
                      onClick={() => setActiveDropdown('product')}
                      ref={productDropdown}
                    >
                      <ProductsIcon />
                    </button>
                    <ProductsDropdown
                      isProductsDropdownActive={activeDropdown === 'product'}
                      setActiveDropdown={setActiveDropdown}
                      target={productDropdown.current}
                    />
                  </li>
                )}
              </>
            )}
            <li>
              <button
                aria-label="Theme Options"
                className={styles['header-icon-button']}
                data-testid="header-content-theme"
                onClick={() => setActiveDropdown('theme')}
                ref={themeDropdown}
              >
                <PaletteIcon />
              </button>
              <ThemeDropdown
                isActive={activeDropdown === 'theme'}
                setActiveDropdown={setActiveDropdown}
                target={themeDropdown.current}
              />
            </li>
            <li>
              <button
                aria-label="Notifications button"
                className={styles['header-icon-button']}
                data-testid="header-notification-center-icon"
                onClick={() => {
                  setActiveDropdown('notification');
                  handleReadAllNotifications();
                  setHasNewNotification(false);
                }}
                ref={notificationDropdown}
              >
                <NotificationIcon />
                {hasNewNotification && (
                  <span
                    className={styles['header-notification-badge']}
                    data-testid="new-notification"
                  ></span>
                )}
              </button>
              <NotificationDropdown
                hasAllNotifications={hasAllNotifications}
                isLoading={isLoading}
                isNotificationDropdownActive={activeDropdown === 'notification'}
                notifications={notifications}
                page={page}
                setActiveDropdown={setActiveDropdown}
                setPage={setPage}
                target={notificationDropdown.current}
              />
            </li>
            <li>
              <button
                aria-label="Account button"
                className={styles['header-content-account-icon']}
                data-testid="header-content-account-icon"
                onClick={() => setActiveDropdown('account')}
                ref={settingsDropdown}
              >
                <p>{firstName?.charAt(0)}</p>
              </button>
              <AccountDropdown
                isAccountDropdownActive={activeDropdown === 'account'}
                setActiveDropdown={setActiveDropdown}
                target={settingsDropdown.current}
              />
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  routes: PropTypes.array,
};

export default Header;
